import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { GENERATE_AUTH_TOKEN } from "../config";
import { Loader2, Gift } from "lucide-react";

interface SubscriptionLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "free-trial-ended" | "plan-limit-reached" | "multi-brand-required";
}

const SubscriptionLimitModal: React.FC<SubscriptionLimitModalProps> = ({
  isOpen,
  onClose,
  type,
}) => {
  const navigate = useNavigate();
  const [monthlyLimit, setMonthlyLimit] = useState<number | null>(null);
  const [nextRenewalDate, setNextRenewalDate] = useState<Date | null>(null);
  const [roundedTotalImages, setRoundedTotalImages] = useState<number>(15);
  const [showExtendTrial, setShowExtendTrial] = useState(false);
  const [shortName, setShortName] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const opinionWhatsAppUrl = `https://wa.me/5491122635392?text=${encodeURIComponent(`Buenas, soy ${shortName || ""} de ${companyName || ""}. Me contactaba para dar mi opinión sobre el programa.`)}`;
  const [showOpinionQR, setShowOpinionQR] = useState(false);
  const opinionQRImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(opinionWhatsAppUrl)}`;
  const [canExtendTrial, setCanExtendTrial] = useState(false);
  const [promoMs, setPromoMs] = useState<number>(0);

  // Recent images carousel (only for plan-limit-reached)
  const [recentImages, setRecentImages] = useState<string[]>([]);
  const [totalThisMonth, setTotalThisMonth] = useState<number>(0);
  const [loadingRecentImages, setLoadingRecentImages] =
    useState<boolean>(false);

  const [openingPortal, setOpeningPortal] = useState(false);

  // Country/provider settings
  const [pais, setPais] = useState<string>("");
  const isArgentina = (pais || "").toLowerCase() === "argentina";

  const handleOpenBillingPortal = async () => {
    try {
      setOpeningPortal(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no autenticado");
      const resp = await fetch(
        "https://ai-agency-dt7d.onrender.com/webhook/billing-portal",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: GENERATE_AUTH_TOKEN,
          },
          body: JSON.stringify({ user_id: user.id }),
        },
      );
      if (!resp.ok)
        throw new Error("No se pudo abrir el portal de facturación");
      const data = await resp.json();
      if (data?.url) {
        window.location.href = data.url as string;
      } else {
        throw new Error("URL inválida recibida");
      }
    } catch (e: any) {
      alert(e?.message || "Error al abrir el portal de facturación");
      setOpeningPortal(false);
    }
  };

  const handleGoToPlan = () => {
    navigate("/subscription");
    onClose();
  };

  // Helpers to compute current billing cycle end based on paid_plan_start_date
  const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const getBillingCycleRange = (
    paidPlanStartDate: string,
    upgradePlanDate?: string | null,
  ) => {
    const now = new Date();
    const subscriptionDate = new Date(paidPlanStartDate);
    const referenceDate = upgradePlanDate
      ? new Date(upgradePlanDate)
      : subscriptionDate;
    const anchorDay = referenceDate.getDate();

    const daysInMonth = (year: number, month: number) =>
      new Date(year, month + 1, 0).getDate();
    const anchorFor = (year: number, month: number) => {
      const dim = daysInMonth(year, month);
      const day = Math.min(anchorDay, dim);
      return new Date(year, month, day, 0, 0, 0, 0);
    };

    let start = anchorFor(now.getFullYear(), now.getMonth());
    if (start.getTime() > now.getTime()) {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start = anchorFor(prevMonth.getFullYear(), prevMonth.getMonth());
    }
    if (start.getTime() < subscriptionDate.getTime()) {
      start = new Date(
        subscriptionDate.getFullYear(),
        subscriptionDate.getMonth(),
        subscriptionDate.getDate(),
        0,
        0,
        0,
        0,
      );
    }
    const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const end = anchorFor(nextMonth.getFullYear(), nextMonth.getMonth());
    return {
      start: start.toISOString(),
      end: end.toISOString(),
      nextRenewal: end,
    };
  };

  // When opening on plan-limit, fetch monthly limit, compute next renewal date,
  // fetch the last 3 generated images and count total images for the current billing cycle.
  useEffect(() => {
    if (!isOpen || type !== "plan-limit-reached") return;
    (async () => {
      try {
        setLoadingRecentImages(true);
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        if (!profile?.id_company) return;
        const idCompany = profile.id_company as string;

        const { data: company } = await supabase
          .from("company_profiles")
          .select("max_fotos_mes, paid_plan_start_date, upgrade_plan_date")
          .eq("id_company", idCompany)
          .single();
        if (!company) return;
        if (typeof company.max_fotos_mes === "number") {
          setMonthlyLimit(company.max_fotos_mes);
        }
        let renewal: Date | null = null;
        let cycleStartIso: string;
        if (company.paid_plan_start_date) {
          const { start, nextRenewal } = getBillingCycleRange(
            company.paid_plan_start_date,
            company.upgrade_plan_date,
          );
          renewal = nextRenewal;
          cycleStartIso = start;
        } else {
          const { start, end } = getCurrentMonthRange();
          renewal = new Date(end);
          cycleStartIso = start;
        }
        setNextRenewalDate(renewal);

        // Count total images generated in the current billing cycle
        try {
          const { count } = await supabase
            .from("imagenes_generadas")
            .select("id", { count: "exact", head: true })
            .eq("id_company", idCompany)
            .neq("has_error", true)
            .gte("created_at", cycleStartIso);
          if (typeof count === "number") setTotalThisMonth(count);
        } catch (e) {
          console.warn("No se pudo contar imágenes del ciclo:", e);
        }

        // Fetch last 3 generated images (non-video, no errors) and sign opt/ URLs
        try {
          const { data: rows } = await supabase
            .from("imagenes_generadas")
            .select("file_name, tipo, has_error, created_at")
            .eq("id_company", idCompany)
            .neq("has_error", true)
            .neq("tipo", "video")
            .not("file_name", "is", null)
            .order("created_at", { ascending: false })
            .limit(3);

          if (Array.isArray(rows) && rows.length > 0) {
            const signed = await Promise.all(
              rows.map(async (row: any) => {
                const fileName = row?.file_name as string | undefined;
                if (!fileName) return null;
                const optPath = `${idCompany}/opt/${fileName}`;
                const fullPath = `${idCompany}/full/${fileName}`;
                const { data: optSigned } = await supabase.storage
                  .from("generadas")
                  .createSignedUrl(optPath, 3600);
                if (optSigned?.signedUrl) return optSigned.signedUrl;
                const { data: fullSigned } = await supabase.storage
                  .from("generadas")
                  .createSignedUrl(fullPath, 3600);
                return fullSigned?.signedUrl || null;
              }),
            );
            setRecentImages(
              signed.filter((u): u is string => typeof u === "string"),
            );
          } else {
            setRecentImages([]);
          }
        } catch (e) {
          console.warn("No se pudieron obtener imágenes recientes:", e);
          setRecentImages([]);
        }
      } catch (e) {
        // Non-blocking: leave defaults
        console.warn("No se pudo obtener datos de límite/renovación:", e);
      } finally {
        setLoadingRecentImages(false);
      }
    })();
  }, [isOpen, type]);

  // Promo countdown: always start at 57:00 when the modal opens in free-trial-ended
  useEffect(() => {
    if (!isOpen || type !== "free-trial-ended") return;
    setPromoMs(57 * 60 * 1000);
    const id = setInterval(() => {
      setPromoMs((prev) => {
        if (prev <= 1000) {
          clearInterval(id);
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [isOpen, type]);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company, name_short")
          .eq("id", user.id)
          .single();
        if ((profile as any)?.name_short)
          setShortName((profile as any).name_short as string);
        if (!profile?.id_company) return;
        const { data: company } = await supabase
          .from("company_profiles")
          .select("nombre, pais")
          .eq("id_company", profile.id_company)
          .single();
        if ((company as any)?.nombre)
          setCompanyName((company as any).nombre as string);
        if ((company as any)?.pais) setPais((company as any).pais as string);
      } catch {}
    })();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || type !== "free-trial-ended") return;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        if (!profile?.id_company) return;

        const { data: company } = await supabase
          .from("company_profiles")
          .select("imagenes_totales_generadas")
          .eq("id_company", profile.id_company)
          .single();
        const total = (company as any)?.imagenes_totales_generadas as
          | number
          | undefined;
        if (typeof total === "number" && total >= 0) {
          const rounded = Math.floor(total / 5) * 5;
          setRoundedTotalImages(Math.max(5, rounded));
        }
        setCanExtendTrial(typeof total === "number" ? total < 22 : false);
      } catch {}
    })();
  }, [isOpen, type]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center">
      <div className="bg-surface-card rounded-lg shadow-md w-full max-w-xl mx-4">
        <div className="p-5">
          {type === "free-trial-ended" ? (
            <>
              {!showExtendTrial || !canExtendTrial ? (
                <>
                  {/* Big centered title */}
                  <h2 className="text-[17px] sm:text-[17px] font-bold text-brand-black/90 text-center mb-4">
                    ¡QUÉDATE CON NOSOTROS!
                  </h2>
                  <hr className="border-neutral-200 mb-5" />

                  {/* Gift offer card */}
                  <div className="mb-6">
                    <div className="rounded-md border border-brand-primary/20 bg-brand-primary/5 p-5 flex items-center justify-center gap-4">
                      <Gift className="w-14 h-14 sm:w-16 sm:h-16 text-brand-primary shrink-0" />
                      <div>
                        <p className="text-[17px] sm:text-[17px] text-neutral-600 leading-tight">
                          Te damos un
                        </p>
                        <p className="text-[17px] sm:text-[17px] font-bold text-brand-primary leading-tight">
                          {isArgentina
                            ? "30 imágenes gratis"
                            : "30% de descuento"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Description text */}
                  <p className="text-[15px] text-neutral-600 mb-6 text-center sm:text-left">
                    Queremos que continues mejorando tu contenido con{" "}
                    <strong className="text-brand-black/90">
                      Estudio Atlas
                    </strong>
                    .{" "}
                    {isArgentina ? (
                      <>
                        Por eso te{" "}
                        <strong className="text-brand-black/90">
                          regalamos 30 imágenes gratis
                        </strong>{" "}
                        en tu{" "}
                        <strong className="text-brand-black/90">
                          primer mes
                        </strong>{" "}
                        de suscripción a cualquiera de los planes.
                      </>
                    ) : (
                      <>
                        Por eso te{" "}
                        <strong className="text-brand-black/90">
                          ofrecemos un 30% de descuento
                        </strong>{" "}
                        en tu{" "}
                        <strong className="text-brand-black/90">
                          primer mes
                        </strong>{" "}
                        de suscripción a cualquiera de los planes.
                      </>
                    )}
                  </p>

                  {/* CTA button */}
                  <button
                    onClick={handleGoToPlan}
                    className="w-full inline-flex items-center justify-center h-10 px-5 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                  >
                    {isArgentina
                      ? "Reclamar mis imágenes gratis"
                      : "Reclamar mi descuento ahora"}
                  </button>
                </>
              ) : (
                <>
                  <h3 className="text-[17px] font-semibold text-brand-black/90 mb-3">
                    Extendé tu prueba gratis
                  </h3>
                  <p className="text-[15px] text-brand-black/90 mb-6">
                    Danos tu opinión sobre que te está pareciendo la plataforma,
                    y recibe a cambio más imágenes gratis para seguir generando.
                    😊
                  </p>
                  <div className="flex justify-end gap-2.5">
                    <button
                      onClick={() => {
                        try {
                          if (
                            window.matchMedia &&
                            window.matchMedia("(min-width: 640px)").matches
                          ) {
                            setShowOpinionQR(true);
                          } else {
                            window.open(opinionWhatsAppUrl, "_blank");
                          }
                        } catch {
                          window.open(opinionWhatsAppUrl, "_blank");
                        }
                      }}
                      className="inline-flex items-center h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                    >
                      Dar opinión
                    </button>
                    <button
                      onClick={() => {
                        setShowExtendTrial(false);
                        setShowOpinionQR(false);
                      }}
                      className="inline-flex items-center h-10 px-4 border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
                    >
                      Volver
                    </button>
                  </div>
                  {showOpinionQR && (
                    <div className="mt-6 hidden sm:flex flex-col items-center">
                      <img
                        src={opinionQRImageUrl}
                        alt="QR para abrir WhatsApp y dar tu opinión"
                        className="w-48 h-48 rounded-md"
                      />
                      <p className="text-[13px] text-neutral-500 mt-3 text-center">
                        Escaneá el código QR con la cámara de tu celular, para
                        dar tu opinión y recibir las imágenes gratis.
                      </p>
                    </div>
                  )}
                </>
              )}
            </>
          ) : type === "plan-limit-reached" ? (
            <>
              {/* Title: celebrate the achievement */}
              <h3 className="text-[17px] sm:text-[19px] font-semibold text-brand-black/90 mb-2 text-center sm:text-left">
                {totalThisMonth > 0 ? (
                  <>¡Generaste {totalThisMonth} imágenes este mes!</>
                ) : (
                  <>Has alcanzado el límite de imágenes de tu plan</>
                )}
              </h3>

              {/* Subtitle */}
              <p className="text-[15px] text-brand-black/90 mb-5 text-center sm:text-left">
                {recentImages.length > 0 || loadingRecentImages ? (
                  <>
                    Aprovechaste todas las imágenes de tu plan. Mirá algunas de
                    las que creaste ↓
                  </>
                ) : monthlyLimit ? (
                  <>Alcanzaste el límite de {monthlyLimit} fotos de este mes.</>
                ) : (
                  <>Alcanzaste el límite de imágenes de tu plan por este mes.</>
                )}
              </p>

              {/* Polaroid carousel: last 3 generated images */}
              {(loadingRecentImages || recentImages.length > 0) && (
                <div className="relative w-full flex items-center justify-center mb-6 h-[190px] sm:h-[230px]">
                  {loadingRecentImages ? (
                    <>
                      {[0, 1, 2].map((i) => {
                        const isCenter = i === 1;
                        const rotations = ["-8deg", "0deg", "8deg"];
                        const translates = ["-55%", "-50%", "-45%"];
                        return (
                          <div
                            key={i}
                            className="absolute left-1/2 top-1/2 w-[130px] h-[160px] sm:w-[150px] sm:h-[185px] rounded-md bg-neutral-100 animate-pulse border border-neutral-200 shadow-sm"
                            style={{
                              transform: `translate(${translates[i]}, -50%) rotate(${rotations[i]})`,
                              zIndex: isCenter ? 10 : 1,
                            }}
                          />
                        );
                      })}
                    </>
                  ) : (
                    recentImages.slice(0, 3).map((url, i) => {
                      // If only 1 or 2 images, center them; with 3 use fan layout
                      const count = Math.min(recentImages.length, 3);
                      const isCenter = count === 3 ? i === 1 : i === 0;
                      const rotations =
                        count === 3
                          ? ["-10deg", "0deg", "10deg"]
                          : count === 2
                            ? ["-6deg", "6deg"]
                            : ["0deg"];
                      const translates =
                        count === 3
                          ? ["-105%", "-50%", "5%"]
                          : count === 2
                            ? ["-85%", "-15%"]
                            : ["-50%"];
                      return (
                        <div
                          key={i}
                          className="absolute left-1/2 top-1/2 w-[130px] h-[160px] sm:w-[150px] sm:h-[185px] rounded-md overflow-hidden bg-surface-card border border-neutral-200 shadow-sm"
                          style={{
                            transform: `translate(${translates[i]}, -50%) rotate(${rotations[i]})`,
                            zIndex: isCenter ? 10 : 1,
                          }}
                        >
                          <img
                            src={url}
                            alt={`Creación reciente ${i + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Renewal date */}
              {nextRenewalDate && (
                <p className="text-[15px] text-neutral-500 mb-2 text-center sm:text-left">
                  Tu cupo de imágenes se renovará el{" "}
                  {nextRenewalDate.toLocaleDateString("es-AR", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                  .
                </p>
              )}

              {/* Upgrade nudge */}
              <p className="text-[15px] text-brand-black/90 mb-5 text-center sm:text-left">
                Mejorá tu plan y obtené hoy{" "}
                <span className="font-semibold">más fotos</span> para seguir
                generando.
              </p>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center sm:justify-end">
                {isArgentina ? (
                  <button
                    onClick={handleGoToPlan}
                    className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                  >
                    Obtené más fotos
                  </button>
                ) : (
                  <button
                    onClick={handleOpenBillingPortal}
                    disabled={openingPortal}
                    className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-70 text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                  >
                    {openingPortal ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Gestionar subscripción"
                    )}
                  </button>
                )}
              </div>
            </>
          ) : type === "multi-brand-required" ? (
            <>
              {/* multi-brand-required: plan gratis no puede crear marcas adicionales */}
              <h3 className="text-[17px] sm:text-[19px] font-semibold text-brand-black/90 mb-2 text-center sm:text-left">
                Crear marcas adicionales requiere un plan pago
              </h3>
              <p className="text-[15px] text-brand-black/90 mb-5 text-center sm:text-left">
                Con un plan pago vas a poder crear y gestionar múltiples marcas dentro de tu cuenta, cada una con su propia identidad visual, productos y modelos.
              </p>
              <p className="text-[15px] text-neutral-600 mb-6 text-center sm:text-left">
                Pasate a un plan pago y desbloqueá esta función al instante.
              </p>
              <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center sm:justify-end">
                <button
                  onClick={onClose}
                  className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleGoToPlan}
                  className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                >
                  Ir a planes
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionLimitModal;
